// Lightweight API client wrapper for the DHMS Family app
// Adds base URL, JSON handling, query params and Authorization header.
// Automatically refreshes tokens on 401 errors

import TokenService from "./tokenService";

// Use environment variable if available, fallback to production URL
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "/family"
    : "https://api.thedhms.com/family");
const API_ROOT = "https://api.thedhms.com";

let isRefreshing = false;
let refreshSubscribers = [];

function buildQuery(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    // booleans and numbers are allowed; arrays become repeated keys
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else {
      searchParams.append(key, value);
    }
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

// Token refresh logic
function onRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

async function refreshAccessToken() {
  const refreshToken = TokenService.getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await fetch(`${API_ROOT}/api/token/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    const data = await response.json();
    // Backend may return { access } or { accessToken } or nested shapes
    const newAccessToken =
      data.access || data.accessToken || data.token || null;
    const newRefreshToken = data.refresh || data.refreshToken || null;

    if (newAccessToken) {
      // Persist tokens via TokenService (will dispatch tokensChanged)
      const tokensToStore = { accessToken: newAccessToken };
      if (newRefreshToken) tokensToStore.refreshToken = newRefreshToken;
      TokenService.storeTokens(tokensToStore);
      return newAccessToken;
    }

    throw new Error("No access token in refresh response");
  } catch (error) {
    // Refresh failed - clear tokens and redirect to login
    TokenService.clearTokens();
    throw error;
  }
}

async function request(
  path,
  {
    method = "GET",
    headers = {},
    body,
    isFormData = false,
    _retry = false,
  } = {}
) {
  const url = path.startsWith("http")
    ? path
    : `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

  const token = TokenService.getAccessToken();
  const finalHeaders = {
    Accept: "application/json",
    ...headers,
  };
  if (token) {
    finalHeaders["Authorization"] = `Bearer ${token}`;
  }
  if (!isFormData && body !== undefined && body !== null) {
    finalHeaders["Content-Type"] =
      finalHeaders["Content-Type"] || "application/json";
  }

  const fetchOptions = {
    method,
    headers: finalHeaders,
  };

  if (body !== undefined && body !== null) {
    fetchOptions.body = isFormData ? body : JSON.stringify(body);
  }

  const res = await fetch(url, fetchOptions);

  const contentType = res.headers.get("content-type") || "";
  let data;
  if (contentType.includes("application/json")) {
    data = await res.json().catch(() => ({}));
  } else if (contentType.includes("text/")) {
    const text = await res.text();
    // Occasionally the backend might return a success message as text
    data = { success: res.ok, message: text };
  } else {
    // For file downloads or unknown types, just return ok flag
    data = { success: res.ok };
  }

  // Handle 401 Unauthorized - try to refresh token
  if (res.status === 401 && !_retry) {
    console.log("[apiClient] 401 Unauthorized - attempting token refresh");

    if (isRefreshing) {
      // Already refreshing, wait for it to complete
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((token) => {
          // Retry original request with new token
          request(path, { method, headers, body, isFormData, _retry: true })
            .then(resolve)
            .catch(reject);
        });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      console.log("[apiClient] Token refreshed successfully");
      isRefreshing = false;
      onRefreshed(newToken);

      // Retry original request with new token
      return request(path, { method, headers, body, isFormData, _retry: true });
    } catch (error) {
      console.error("[apiClient] Token refresh failed:", error);
      isRefreshing = false;

      // Redirect to login
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        window.location.href = "/login?session_expired=true";
      }

      throw new Error("Session expired. Please log in again.");
    }
  }

  if (!res.ok) {
    const message =
      data?.message || data?.detail || `Request failed (${res.status})`;
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const apiClient = {
  get: (path, params, options = {}) =>
    request(`${path}${buildQuery(params)}`, { method: "GET", ...options }),
  post: (path, body, options = {}) =>
    request(path, { method: "POST", body, ...options }),
  put: (path, body, options = {}) =>
    request(path, { method: "PUT", body, ...options }),
  patch: (path, body, options = {}) =>
    request(path, { method: "PATCH", body, ...options }),
  delete: (path, params, options = {}) =>
    request(`${path}${buildQuery(params)}`, { method: "DELETE", ...options }),
  upload: (path, formData, options = {}) =>
    request(path, { method: "POST", body: formData, isFormData: true, ...options }),

  // Fetches a file and returns { blob, filename } so the caller can trigger a browser download.
  // Follows the same 401 → refresh → retry → logout pattern as request().
  async download(path, _retry = false) {
    const url = path.startsWith("http")
      ? path
      : `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

    const token = TokenService.getAccessToken();
    const headers = { Accept: "*/*" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, { method: "GET", headers });

    if (res.status === 401 && !_retry) {
      try {
        await refreshAccessToken();
        return this.download(path, true);
      } catch {
        TokenService.clearTokens();
        if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
          window.location.href = "/login?session_expired=true";
        }
        throw new Error("Session expired. Please log in again.");
      }
    }

    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`);
    }

    // Extract filename from Content-Disposition if the server sends it
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    const filename = match ? match[2].trim() : "faulty-devices.csv";

    const blob = await res.blob();
    return { blob, filename };
  },

  baseUrl: BASE_URL,
};

export default apiClient;
